import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'
import type { StoryQuestion } from '@/sanskrit/lib/content/stories'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import { ArrowLeft, Check, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Seo } from '@/sanskrit/components/generals/seo'
import { JsonLd, breadcrumbList } from '@/components/generals/json-ld'
import { Layout } from '@/sanskrit/components/generals/layout'
import { useAppState, useT } from '@/sanskrit/components/providers/app-state'
import { STORIES, getStory } from '@/sanskrit/lib/content/stories'
import { markStoryRead } from '@/sanskrit/lib/progress/engine'
import { useSession } from '@/lib/client/session'
import { SanskritGate } from '@/components/plans/sanskrit-gate'
import StoryReader from '@/sanskrit/components/story/story-reader'
import Skt from '@/sanskrit/components/vani/skt'

interface StoryPageProps {
    id: string
}

const Question: FC<{ question: StoryQuestion, index: number }> = ({ question, index }): ReactNode => {
    const t = useT()
    const [picked, setPicked] = useState<number | null>(null)
    const answered = picked !== null

    return (
        <li className='rounded-[1.75rem] border border-border bg-card p-5 shadow-sm'>
            <p className='text-xs font-semibold text-muted-foreground'>{t({ hi: `प्रश्न ${index + 1}`, en: `Question ${index + 1}` })}</p>
            <p className='mt-1 font-semibold text-foreground'>{t(question.q)}</p>
            {question.sa && <Skt text={question.sa} as='p' className='deva mt-1 text-lg text-ink' />}
            <div className='mt-4 grid gap-2 sm:grid-cols-2'>
                {question.options.map((option, i) => {
                    const correct = i === question.answer
                    const chosen = picked === i

                    return (
                        <button
                            key={i}
                            type='button'
                            disabled={answered}
                            onClick={() => setPicked(i)}
                            aria-pressed={chosen}
                            className={cn(
                                'flex min-h-12 items-center justify-between gap-2 rounded-2xl border-2 px-4 py-2 text-left font-medium transition-colors',
                                !answered && 'border-border bg-background hover:border-saffron hover:bg-saffron-soft',
                                answered && correct && 'border-tulsi bg-tulsi-soft text-tulsi',
                                answered && chosen && !correct && 'border-destructive/60 bg-destructive/10 text-destructive',
                                answered && !chosen && !correct && 'border-border opacity-60'
                            )}
                        >
                            <span lang={typeof option === 'string' ? 'sa' : undefined} className={typeof option === 'string' ? 'deva text-lg' : undefined}>
                                {typeof option === 'string' ? option : t(option)}
                            </span>
                            {answered && correct && <Check className='size-5 shrink-0' aria-label={t({ hi: 'सही', en: 'Correct' })} />}
                            {answered && chosen && !correct && <X className='size-5 shrink-0' aria-label={t({ hi: 'ग़लत', en: 'Not quite' })} />}
                        </button>
                    )
                })}
            </div>
            {answered && (
                <p role='status' className='mt-3 rounded-2xl bg-muted px-4 py-3 text-sm text-foreground'>
                    {t(question.explain)}
                </p>
            )}
        </li>
    )
}

const StoryPage: FC<StoryPageProps> = ({ id }): ReactNode => {
    const t = useT()
    const { updateProgress, hydrated } = useAppState()
    const { access } = useSession()
    const story = getStory(id)

    // Counted as read only when it is open to this visitor (stories are Sanskrit Premium).
    useEffect(() => {
        if (hydrated && story && access.sanskrit) updateProgress(p => markStoryRead(p, story.id))
    }, [hydrated, story, access.sanskrit, updateProgress])

    if (!story) return null

    return (
        <Layout>
            <Seo
                title={`${story.title.en} (${story.sa})`}
                description={`${story.summary.en} A graded Sanskrit story you can hear whole or word by word, with every word's meaning one tap away and questions.`}
                ogImage={story.image}
            />
            <JsonLd
                id='breadcrumbs'
                data={breadcrumbList([
                    { name: 'Suvaani', path: '/' },
                    { name: 'Sanskrit', path: '/sanskrit' },
                    { name: 'Stories', path: '/sanskrit/stories' },
                    { name: story.title.en, path: `/sanskrit/stories/${story.id}` }
                ])}
            />

            <div className='mx-auto max-w-3xl px-4 pt-6 pb-16 sm:px-6'>
                <Link href='/sanskrit/stories' className='inline-flex min-h-11 items-center gap-1.5 text-sm font-semibold text-saffron-strong hover:underline'>
                    <ArrowLeft className='size-4' aria-hidden='true' />
                    {t({ hi: 'सभी कथाएँ', en: 'All stories' })}
                </Link>

                <SanskritGate what='This story'>
                    <header className='mt-4 mb-6'>
                        <p lang='sa' className='literary text-lg text-saffron-strong'>{story.sa}</p>
                        <h1 className='font-heading text-3xl text-foreground sm:text-4xl'>{t(story.title)}</h1>
                        <p className='mt-2 text-muted-foreground'>{t(story.summary)}</p>
                        <p className='mt-2 text-sm text-foreground/80'><span className='font-semibold'>{t({ hi: 'ध्यान दें:', en: 'Focus:' })}</span> {t(story.focus)}</p>
                    </header>

                    <StoryReader story={story} />

                    {story.questions.length > 0 && (
                        <section aria-labelledby='story-questions' className='mt-10'>
                            <p lang='sa' className='deva text-sm font-semibold text-saffron-strong'>प्रश्नाः</p>
                            <h2 id='story-questions' className='font-heading text-2xl text-foreground'>{t({ hi: 'समझ के प्रश्न', en: 'Check your understanding' })}</h2>
                            <ol className='mt-4 flex flex-col gap-4'>
                                {story.questions.map((q, i) => <Question key={i} question={q} index={i} />)}
                            </ol>
                        </section>
                    )}
                </SanskritGate>
            </div>
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => ({
    paths: STORIES.map(s => ({ params: { id: s.id } })),
    fallback: false
})

export const getStaticProps: GetStaticProps<StoryPageProps> = async ({ params }) => {
    const id = String(params?.id ?? '')

    if (!getStory(id)) return { notFound: true }

    return { props: { id } }
}

export default StoryPage
